import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Globe, ChevronDown, ArrowLeft } from "lucide-react";
import SocialLinks from "@/components/SocialLinks";
import { PUBLIC_PALETTE } from "@/lib/publicPalette";

const LANGS = [
  { code: "it", label: "IT" }, { code: "en", label: "EN" },
  { code: "fr", label: "FR" }, { code: "es", label: "ES" }, { code: "de", label: "DE" },
];

const L = {
  it: { catalog: "Catalogo", details: "Vedi dettaglio", company: "Azienda", contact: "Contatti",
        web: "Sito web", address: "Sede", vat: "P.IVA", email: "Email", phone: "Telefono",
        no_products: "Nessun prodotto in questa categoria.",
        back_to_categories: "Torna alle categorie",
        products_count: (n) => `${n} ${n === 1 ? "prodotto" : "prodotti"}` },
  en: { catalog: "Catalogue", details: "View details", company: "Company", contact: "Contact",
        web: "Website", address: "Address", vat: "VAT", email: "Email", phone: "Phone",
        no_products: "No products in this category.",
        back_to_categories: "Back to categories",
        products_count: (n) => `${n} ${n === 1 ? "product" : "products"}` },
  fr: { catalog: "Catalogue", details: "Voir détails", company: "Société", contact: "Contact",
        web: "Site", address: "Adresse", vat: "TVA", email: "Email", phone: "Téléphone",
        no_products: "Aucun produit dans cette catégorie.",
        back_to_categories: "Retour aux catégories",
        products_count: (n) => `${n} ${n === 1 ? "produit" : "produits"}` },
  es: { catalog: "Catálogo", details: "Ver detalles", company: "Empresa", contact: "Contacto",
        web: "Web", address: "Dirección", vat: "NIF", email: "Email", phone: "Teléfono",
        no_products: "Sin productos en esta categoría.",
        back_to_categories: "Volver a categorías",
        products_count: (n) => `${n} ${n === 1 ? "producto" : "productos"}` },
  de: { catalog: "Katalog", details: "Details ansehen", company: "Firma", contact: "Kontakt",
        web: "Webseite", address: "Adresse", vat: "USt-IdNr.", email: "Email", phone: "Telefon",
        no_products: "Keine Produkte in dieser Kategorie.",
        back_to_categories: "Zurück zu Kategorien",
        products_count: (n) => `${n} ${n === 1 ? "Produkt" : "Produkte"}` },
};

const CURRENCY_SYMBOL = { EUR: "€", USD: "$", GBP: "£" };

export default function BrandCatalog() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [brand, setBrand] = useState(null);
  const [layout, setLayout] = useState([]);
  const [openCat, setOpenCat] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [lang, setLang] = useState("it");
  const [showLangs, setShowLangs] = useState(false);
  const t = useMemo(() => L[lang] || L.it, [lang]);
  const productsRef = useRef(null);
  const accordionRef = useRef(null);

  useEffect(() => {
    document.title = "Cosmely · Catalogo";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    api.get(`/public/brands/${slug}`).then(r => setBrand(r.data))
      .catch(() => nav("/disabled"));
    api.get(`/public/vetrina-layout`, { params: { brand_slug: slug } }).then(r => setLayout(r.data)).catch(()=>{});
  }, [slug, nav]);

  const loadProducts = async (cid, sid) => {
    setProductsLoading(true);
    // Scroll to products area immediately so user sees skeleton feedback
    requestAnimationFrame(() => {
      if (productsRef.current) {
        const y = productsRef.current.getBoundingClientRect().top + window.scrollY - 56;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    });
    try {
      const q = sid ? `subcategory_id=${sid}` : `category_id=${cid}`;
      const r = await api.get(`/public/brands/${slug}/products?${q}`);
      setProducts(r.data);
    } catch { setProducts([]); }
    finally { setProductsLoading(false); }
  };

  if (!brand) {
    return (
      <div className="public-page flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#8A8579]"/>
      </div>
    );
  }

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const enabledFields = new Set((layout || []).filter(b => b.is_enabled).map(b => b.block_key));
  const fieldOrder = (layout || []).filter(b => b.is_enabled).map(b => b.block_key);
  const priceStr = (v, cur) => (v == null ? null : `${CURRENCY_SYMBOL[cur] || cur || "€"} ${Number(v).toFixed(2).replace(".", ",")}`);

  const toggleCat = (cid) => {
    setOpenCat(o => o === cid ? null : cid);
    setActiveCat(null);
    setSelectedSub(null);
    setProducts([]);
  };
  const pickSub = (cid, sid) => {
    setActiveCat(cid);
    setSelectedSub(sid);
    setOpenCat(null); // auto-collapse category once a sub is chosen
    loadProducts(cid, sid);
  };
  const resetToCategories = () => {
    setActiveCat(null);
    setSelectedSub(null);
    setProducts([]);
    setOpenCat(null);
    requestAnimationFrame(() => {
      if (accordionRef.current) {
        const y = accordionRef.current.getBoundingClientRect().top + window.scrollY - 56;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    });
  };

  const cats = brand.categories || [];
  const activeCategory = activeCat ? cats.find(c => c.id === activeCat) : null;
  const activeSubcategory = activeCategory && selectedSub
    ? (activeCategory.subcategories || []).find(s => s.id === selectedSub)
    : null;
  const showProducts = !!activeCat;

  return (
    <div className="public-page"
          data-testid="brand-catalog"
          style={PUBLIC_PALETTE}>
      <header className="sticky top-0 z-30 backdrop-blur"
              style={{ borderBottom: `1px solid var(--brand)`, background: "var(--surface)" }}>
        <div className="flex justify-between items-center px-5 py-3">
          <div className="text-[10px] tracking-[0.3em] uppercase font-medium" style={{ color: "var(--brand)" }}>
            Cosmely
          </div>
          <div className="relative">
            <button onClick={()=>setShowLangs(s=>!s)}
                    className="text-[11px] tracking-[0.25em] uppercase flex items-center gap-1.5"
                    style={{ color: "var(--brand)" }}
                    data-testid="lang-selector">
              <Globe className="w-3.5 h-3.5" strokeWidth={1.5}/>
              {lang.toUpperCase()}
              <ChevronDown className="w-3 h-3" strokeWidth={1.5}/>
            </button>
            {showLangs && (
              <div className="absolute right-0 top-full mt-2 z-50 min-w-[80px]"
                    style={{ border: `1px solid var(--brand)`, background: "var(--surface)" }}>
                {LANGS.map(l => (
                  <button key={l.code}
                          onClick={()=>{ setLang(l.code); setShowLangs(false); }}
                          className={`block px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase w-full text-left transition-colors pp-lang-opt ${l.code === lang ? "font-semibold" : ""}`}
                          data-testid={`lang-option-${l.code}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <article className="pb-6">
        <div className="pp-frame">
          {/* Brand mark */}
          <div className="pp-block pp-brand-mark" data-testid="bc-brand-mark">
            {brand.logo_url ? (
              <img src={`${apiBase}${brand.logo_url}`} alt={brand.name} style={{ maxHeight: 64 }}/>
            ) : (
              <h1 className="pp-title text-center">{brand.name}</h1>
            )}
          </div>

          {brand.description && (
            <div className="pp-block" data-testid="bc-brand-description">
              <div className="pp-block__body prose-rt" style={{ textAlign: "center" }}
                    dangerouslySetInnerHTML={{ __html: brand.description }}/>
            </div>
          )}

          {/* Accordion categories */}
          <div className="pp-block" style={{ padding: 0 }} data-testid="bc-accordion-wrap">
            <div className="bc-accordion" ref={accordionRef}>
              {cats.length === 0 && (
                <div className="p-6 text-center text-sm" style={{ color: "var(--on-surface-muted)" }}
                     data-testid="bc-empty-catalog">
                  Catalogo in aggiornamento.
                </div>
              )}
              {cats.map(c => (
                <div key={c.id} className="bc-accordion-item" data-open={openCat === c.id}
                     data-testid={`bc-cat-${c.id}`}>
                  <button className="bc-accordion-header" onClick={() => toggleCat(c.id)}
                          data-testid={`bc-cat-btn-${c.id}`}>
                    <span>{c.name}</span>
                    <span className="flex items-center">
                      <span className="bc-accordion-header__count">{c.product_count}</span>
                      <ChevronDown className="w-4 h-4 bc-accordion-chevron" strokeWidth={1.5}/>
                    </span>
                  </button>
                  <div className="bc-accordion-content">
                    {(c.subcategories || []).map(s => (
                      <button key={s.id} className="bc-subcat-item"
                              data-selected={selectedSub === s.id}
                              onClick={() => pickSub(c.id, s.id)}
                              data-testid={`bc-subcat-${s.id}`}>
                        <span>{s.name}</span>
                        <span className="bc-subcat-item__count">{s.product_count}</span>
                      </button>
                    ))}
                    {/* If category has direct products (no sub) — allow selecting the top-level */}
                    {(!c.subcategories || c.subcategories.length === 0) && c.product_count > 0 && (
                      <button className="bc-subcat-item"
                              data-selected={selectedSub === null && activeCat === c.id}
                              onClick={() => { setActiveCat(c.id); setSelectedSub(null); setOpenCat(null); loadProducts(c.id, null); }}
                              data-testid={`bc-cat-only-${c.id}`}>
                        <span>{t.catalog}</span>
                        <span className="bc-subcat-item__count">{c.product_count}</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Product list */}
          {showProducts && (
            <div className="pp-block" style={{ padding: 0 }} data-testid="bc-products-wrap" ref={productsRef}>
              {/* Breadcrumb sticky */}
              <div className="bc-breadcrumb" data-testid="bc-breadcrumb">
                <button className="bc-breadcrumb__back"
                        onClick={resetToCategories}
                        data-testid="bc-breadcrumb-back">
                  <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5}/>
                  <span>{t.back_to_categories}</span>
                </button>
                <div className="bc-breadcrumb__trail">
                  {activeCategory && (
                    <>
                      <span className="bc-breadcrumb__cat">{activeCategory.name}</span>
                      {activeSubcategory && (
                        <>
                          <span className="bc-breadcrumb__sep">›</span>
                          <span className="bc-breadcrumb__sub">{activeSubcategory.name}</span>
                        </>
                      )}
                    </>
                  )}
                </div>
                {!productsLoading && products.length > 0 && (
                  <div className="bc-breadcrumb__count">{t.products_count(products.length)}</div>
                )}
              </div>

              {productsLoading ? (
                <div className="bc-product-list" data-testid="bc-products-skeleton">
                  {[0,1,2].map(i => (
                    <div key={i} className="bc-product-card bc-skeleton-card">
                      <div className="bc-skeleton bc-skeleton--image"/>
                      <div className="bc-skeleton bc-skeleton--line" style={{width:"70%"}}/>
                      <div className="bc-skeleton bc-skeleton--line" style={{width:"40%"}}/>
                      <div className="bc-skeleton bc-skeleton--line" style={{width:"55%"}}/>
                    </div>
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="p-6 text-center text-sm" style={{ color: "var(--on-surface-muted)" }}>
                  {t.no_products}
                </div>
              ) : (
                <div className="bc-product-list bc-product-list--enter">
                  {products.map((p, i) => (
                    <div key={p.id} style={{ animationDelay: `${Math.min(i, 6) * 60}ms` }}
                         className="bc-product-card-wrap">
                      <ProductCard p={p} apiBase={apiBase}
                                    enabledFields={enabledFields} fieldOrder={fieldOrder}
                                    t={t} priceStr={priceStr}
                                    onOpen={() => nav(`/b/${slug}/p/${p.public_slug}`)}/>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <BrandFooter brand={brand} t={t}
                     socialEnabled={enabledFields.has("social")}/>
      </article>
    </div>
  );
}

function ProductCard({ p, apiBase, enabledFields, fieldOrder, t, priceStr, onOpen }) {
  const price = priceStr(p.price_value, p.price_currency);
  // Sub label prefers subcategory; falls back to category.
  const subLabel = p.subcategory || p.category;

  const rendered = fieldOrder.map(key => {
    switch (key) {
      case "photo":
        return p.photo_url ? (
          <div key="photo" className="bc-product-image">
            <img src={`${apiBase}${p.photo_url}`} alt={p.name}/>
          </div>
        ) : null;
      case "name":
        return <div key="name" className="bc-product-name">{p.name}</div>;
      case "subcategory":
        return subLabel ? <div key="sub" className="bc-product-sub">{subLabel}</div> : null;
      case "quantity":
        return p.quantity_value ? (
          <div key="qty" className="bc-product-meta">
            <span>{p.quantity_value} {p.udm}</span>
          </div>
        ) : null;
      case "price":
        return price ? (
          <div key="price" className="bc-product-meta">
            <span className="bc-price">{price}</span>
          </div>
        ) : null;
      case "cta":
        return p.public_slug ? (
          <button key="cta" onClick={onOpen} className="pp-btn" data-testid={`bc-detail-${p.public_slug}`}>
            {t.details}
          </button>
        ) : null;
      default:
        return null;
    }
  }).filter(Boolean);

  return (
    <div className="bc-product-card" data-testid={`bc-product-${p.public_slug || p.id}`}>
      {rendered}
    </div>
  );
}

function BrandFooter({ brand, t, socialEnabled }) {
  const rows = [
    { key: "company", label: t.company, value: brand.company_name },
    { key: "address", label: t.address, value: brand.address },
    { key: "vat", label: t.vat, value: brand.vat_number },
    { key: "email", label: t.email, value: brand.email, href: brand.email ? `mailto:${brand.email}` : null },
    { key: "phone", label: t.phone, value: brand.phone, href: brand.phone ? `tel:${brand.phone}` : null },
    { key: "web", label: t.web, value: brand.website, href: brand.website || null },
  ].filter(r => r.value);
  const hasSocial = socialEnabled && [brand.instagram_url, brand.facebook_url,
                                        brand.linkedin_url, brand.x_url]
    .some(u => (u || "").trim().length > 0);
  if (rows.length === 0 && !hasSocial) return null;
  return (
    <footer className="bc-footer" data-testid="bc-footer">
      {rows.map(r => (
        <div key={r.key} className="bc-footer__block" data-testid={`bc-footer-${r.key}`}>
          <div className="bc-footer__title">{r.label}</div>
          <div className="bc-footer__value">
            {r.href ? <a href={r.href} target={r.key === "web" ? "_blank" : undefined} rel="noopener noreferrer">{r.value}</a> : r.value}
          </div>
        </div>
      ))}
      {hasSocial && (
        <div className="bc-footer__social" data-testid="bc-footer-social">
          <SocialLinks urls={{
            instagram_url: brand.instagram_url,
            facebook_url: brand.facebook_url,
            linkedin_url: brand.linkedin_url,
            x_url: brand.x_url,
          }} size="md" className="justify-center gap-6"/>
        </div>
      )}
    </footer>
  );
}
